/**
 * Notification centre (UX-005 §3; UX-002 A4). Paginated list of notifications
 * with read/unread state and class badges (immediate vs digest). Items link
 * to their record via record_route.
 *
 * Two declared seams, both stated rather than papered over:
 *
 * 1. There is no notification-item store in the schema (only
 *    `notification_channels` and a per-user `notification_policy`), so an empty
 *    list means "nothing can produce one", NOT "you have no notifications". The
 *    empty state is therefore `no_data`. `verified_quiet` is rendered ONLY when
 *    the server says so in `meta.quiet` — UX-003 principle 5: verified-quiet and
 *    no-data are never interchangeable, and this screen must not assert a healthy
 *    data path it never checked.
 * 2. `PATCH /v1/notifications/{id}` is in the contract but has no backend route,
 *    so "Mark read" would 404. The button is rendered disabled with its reason
 *    rather than left inert-but-enabled.
 */
import { type ReactNode } from "react";
import { useGetV1Notifications } from "@solarkey/api-client";
import { Skeleton, ErrorState, EmptyState } from "@solarkey/ui";
import { BackButton } from "../shell/BackButton";

/** Stated on the disabled control, so the reader learns why and not just that. */
const MARK_READ_UNAVAILABLE = "Marking read is not available yet: the platform has no notification store to write to.";

export function NotificationsScreen(props: {
  scope?: string;
}): ReactNode {
  const query = useGetV1Notifications({ scope: props.scope });

  if (query.isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <BackButton />
        <h1 style={h1}>Notifications</h1>
      </div>
        <Skeleton variant="text" lines={6} />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="Couldn't load notifications"
        onRetry={() => void query.refetch()}
      />
    );
  }

  const items = query.data.data.data?.items ?? [];
  // Verified-quiet is the SERVER's statement, never the client's inference from an
  // empty array. Absent `meta.quiet` → this is a no-data seam (see the file note).
  const quiet = query.data.data.meta?.quiet;
  const isVerifiedQuiet = quiet?.is_quiet === true;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <BackButton />
        <h1 style={h1}>Notifications</h1>
      </div>

      {items.length === 0 ? (
        isVerifiedQuiet ? (
          <EmptyState
            variant="verified_quiet"
            title="All caught up"
            message={quiet?.text ?? "Checked, and there is nothing to show."}
          />
        ) : (
          <EmptyState
            variant="no_data"
            title="Not measured"
            message="No notifications are being recorded yet — the platform has no notification store, so this list cannot say whether anything happened. It is a declared seam, not an empty inbox."
          />
        )
      ) : (
        <ul style={list}>
          {items.map((n, i) => (
            <li
              key={n.id ?? i}
              style={{
                ...notifRow,
                background: n.read ? "transparent" : "var(--sk-color-surface-2)",
              }}
            >
              <div style={notifTop}>
                <span
                  className="sk-chip"
                  style={n.class === "immediate" ? chipImmediate : chipDigest}
                >
                  {n.class ?? "notification"}
                </span>
                <time style={timestamp}>
                  {n.created_at ? formatTime(n.created_at) : ""}
                </time>
              </div>

              <p style={notifText}>{n.text ?? ""}</p>

              <div style={notifActions}>
                {n.record_route ? (
                  <span style={linkText}>→ {n.record_route}</span>
                ) : null}
                {!n.read && n.id ? (
                  <button
                    type="button"
                    style={markBtn}
                    disabled
                    title={MARK_READ_UNAVAILABLE}
                    aria-describedby={`markread-why-${n.id}`}
                  >
                    Mark read
                  </button>
                ) : null}
                {!n.read && n.id ? (
                  <span id={`markread-why-${n.id}`} style={disabledWhy}>
                    not available yet — no notification store to write to
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * A notification's time, stamped with the zone it is actually shown in. The
 * required basis is the site's own `platform_sites.timezone`, which the
 * notification contract does not carry. These are platform-level notices rather
 * than readings from a plant, so they render in the READER's own zone — the one
 * they think in — and name it, so it is never left to be assumed. They used to
 * render in UTC, which was honest but made an Indian reader subtract five and a
 * half hours from every line.
 */
function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const shown = d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${shown} ${zoneShortName()}`;
  } catch {
    return iso;
  }
}

/** What to CALL the reader's own zone — "IST" under en-IN, "GMT+5:30" where the
 *  locale has no abbreviation. Read from the browser, never hardcoded: a literal
 *  zone in the platform is banned (the timezone is per-site config), and a
 *  hardcoded one would be wrong for every reader outside it. */
function zoneShortName(): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
      .formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value
      ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "";
  }
}

const h1: React.CSSProperties = {
  margin: 0,
  font: "600 20px/1.25 var(--sk-font-text)",
  color: "var(--sk-color-ink-1)",
};
const list: React.CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: 1,
};
const notifRow: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: "10px 12px",
  borderRadius: "var(--sk-radius-card)",
  border: "1px solid var(--sk-color-border-1)",
};
const notifTop: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};
const chipImmediate: React.CSSProperties = {
  background: "var(--sk-color-accent)",
  color: "var(--sk-color-surface-0)",
};
const chipDigest: React.CSSProperties = {};
const timestamp: React.CSSProperties = {
  font: "400 11px/1.3 var(--sk-font-mono)",
  color: "var(--sk-color-ink-3)",
};
const notifText: React.CSSProperties = {
  margin: 0,
  font: "400 13px/1.45 var(--sk-font-text)",
  color: "var(--sk-color-ink-1)",
};
const notifActions: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 8,
};
const linkText: React.CSSProperties = {
  font: "400 11px/1.3 var(--sk-font-text)",
  color: "var(--sk-color-accent)",
};
const markBtn: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-card)",
  padding: "3px 8px",
  font: "500 11px/1.3 var(--sk-font-text)",
  color: "var(--sk-color-ink-3)",
  cursor: "not-allowed",
};
const disabledWhy: React.CSSProperties = {
  font: "400 10px/1.3 var(--sk-font-text)",
  color: "var(--sk-color-ink-3)",
};
